
import * as React from 'react';

interface IAsyncState<TProps> {
  component?: React.ComponentType<TProps> | undefined;
}

/**
 * @public
 */
export interface IModuleWithDefault<T> {
  default: T;
}

/**
 * Produces a component which internally resolves asynchronously before first mount.
 * The component passes all props through to the loaded component.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<{ default: React.ComponentType<TProps>; }>
): React.ComponentType<TProps>;
/**
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<React.ComponentType<TProps>>
): React.ComponentType<TProps>;
/**
 * @internal
 */
export function asAsync<TProps>(
  load: () => Promise<React.ComponentType<TProps> | IModuleWithDefault<React.ComponentType<TProps>>>
): React.ComponentType<TProps> {
  class Async extends React.Component<TProps, IAsyncState<TProps>> {
    private _isDisposed: boolean | undefined;

    constructor(props: TProps) {
      super(props);

      this.state = {};
    }

    public render(): JSX.Element | null {
      const {
        component: Component
      } = this.state;

      return Component ?
        (
          <Component { ...this.props } />
        ) :
        null;
    }

    public async componentWillMount(): Promise<void> {
      // Resolve the component during this lifecycle method so
      // if the promise resolves synchronously, setState does not trigger another update.
      const componentModule = await load();

      if (!this._isDisposed) {
        this.setState({
          component: getModuleDefaultOrModule(componentModule)
        });
      }
    }

    public componentWillUnmount(): void {
      this._isDisposed = true;
    }
  }

  return Async;
}

function getModuleDefaultOrModule<T>(valueOrModuleWithDefault: T | IModuleWithDefault<T>): T {
  return (valueOrModuleWithDefault as IModuleWithDefault<T>).default || valueOrModuleWithDefault;
}
